import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2, Gem, CalendarDays, Phone, Mail, Globe, MapPin, MessageCircle, Facebook, Instagram, Star } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { CardSide } from "@/components/card/CardSide";
import { extractFields, withSystem, setBrandCache } from "@/lib/card";

const pretty = (k) => k.toLowerCase().split("_").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" ");

const useWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(360);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

export default function Verify() {
  const { rid } = useParams();
  const [res, setRes] = useState(null);
  const [ref, width] = useWidth();

  useEffect(() => {
    api.get(`/public/verify/${rid}`).then((r) => setRes(r.data)).catch(() => setRes(false));
  }, [rid]);

  if (res === null) return <div className="min-h-screen grid place-items-center bg-[#FFFBEB]"><Loader2 className="animate-spin text-amber-700" /></div>;

  if (!res) return (
    <div className="min-h-screen grid place-items-center bg-[#FFFBEB] text-stone-800 p-6" data-testid="verify-not-found">
      <div className="text-center max-w-sm">
        <ShieldX size={48} className="mx-auto text-red-600" />
        <h1 className="font-heading text-2xl font-extrabold mt-4">Certificate not found</h1>
        <p className="text-sm text-stone-600 mt-2">This certificate could not be verified. Please contact the issuing jeweller.</p>
        <Link to="/verify" className="inline-block mt-4 text-sm font-semibold text-amber-800 hover:underline" data-testid="verify-search-link">Search by certificate number →</Link>
      </div>
    </div>
  );

  const { template: tpl, data, issuer, issued_at } = res;
  const brand = res.brand || {};
  const storeName = brand.store_name || issuer;
  const fields = extractFields(tpl);
  const photos = fields.filter((f) => f.type === "image" && data[f.key]);
  const texts = fields.filter((f) => f.type === "text" && String(data[f.key] ?? "").trim());
  const scale = Math.min((width - 2) / tpl.width, 9);
  setBrandCache(brand);
  const sysData = withSystem({ id: res.record_id, data });

  return (
    <div className="min-h-screen bg-[#FFFBEB] text-stone-900" data-testid="verify-page">
      <div className="max-w-xl mx-auto px-5 py-10 space-y-8">
        <header className="text-center rise" data-testid="verify-brand-header">
          {brand.logo && <img src={assetUrl(brand.logo)} alt={storeName} className="h-16 max-w-[200px] object-contain mx-auto mb-3" data-testid="verify-brand-logo" />}
          {brand.store_name && <p className="text-lg font-bold text-amber-900 mb-1" style={{ fontFamily: "Cinzel, serif" }} data-testid="verify-brand-name">{brand.store_name}</p>}
          {brand.tagline && <p className="text-xs text-stone-500 mb-4" data-testid="verify-brand-tagline">{brand.tagline}</p>}
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-700 flex items-center justify-center gap-1.5"><Gem size={12} /> Certificate of Authenticity</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-2" style={{ fontFamily: "Cinzel, serif" }} data-testid="verify-title">{tpl.name}</h1>
          {storeName && <p className="text-sm text-stone-500 mt-1">Issued by {storeName}</p>}
        </header>

        <div className="rounded-2xl border border-emerald-600/30 bg-emerald-50 px-5 py-4 flex items-center gap-4 rise" style={{ animationDelay: "80ms" }} data-testid="verify-status">
          <ShieldCheck size={36} className="text-emerald-600 shrink-0" />
          <div>
            <p className="font-heading font-extrabold text-emerald-800">Verified genuine</p>
            <p className="text-xs text-emerald-700/80">This certificate is registered in the issuer's records.</p>
          </div>
        </div>

        {photos.map((f) => (
          <img key={f.key} src={assetUrl(data[f.key])} alt={pretty(f.key)} className="w-full aspect-square object-cover rounded-2xl border border-amber-200 shadow-lg rise" data-testid={`verify-photo-${f.key}`} />
        ))}

        <dl className="rounded-2xl border border-amber-200 bg-white divide-y divide-amber-100 rise" style={{ animationDelay: "160ms" }} data-testid="verify-details">
          {texts.map((f) => (
            <div key={f.key} className="flex justify-between gap-4 px-5 py-3">
              <dt className="text-xs uppercase tracking-wider text-amber-800/70 font-semibold">{pretty(f.key)}</dt>
              <dd className="text-sm font-semibold text-right" data-testid={`verify-field-${f.key}`}>{String(data[f.key])}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-4 px-5 py-3">
            <dt className="text-xs uppercase tracking-wider text-amber-800/70 font-semibold flex items-center gap-1"><CalendarDays size={12} /> Registered</dt>
            <dd className="text-sm font-semibold">{new Date(issued_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</dd>
          </div>
        </dl>

        <div ref={ref} className="space-y-4">
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 text-center">Card on record</p>
          {["front", ...(tpl.double_sided !== false ? ["back"] : [])].map((s) => (
            <div key={s} className="shadow-xl rounded-xl overflow-hidden w-fit mx-auto">
              <CardSide tpl={tpl} side={s} data={sysData} scale={scale} rounded testId={`verify-card-${s}`} />
            </div>
          ))}
        </div>
        <StoreContact brand={brand} name={storeName} />
        {res.site && (
          <a href={res.site.domain ? `https://${res.site.domain}` : `/store/${res.site.slug}`} className="block text-center rounded-full bg-stone-900 text-amber-100 py-3 text-sm font-semibold hover:bg-stone-800" data-testid="verify-visit-showroom">Visit {storeName || "the"} showroom website →</a>
        )}
        <Link to="/verify" className="block text-center text-sm font-semibold text-amber-800 hover:underline" data-testid="verify-another-link">Verify another certificate →</Link>
        <p className="text-center text-[11px] text-stone-400 font-mono">Ref {res.record_id.slice(0, 8).toUpperCase()}</p>
      </div>
    </div>
  );
}

const digits = (v) => String(v).replace(/[^\d+]/g, "");
const site = (v) => (/^https?:\/\//.test(v) ? v : `https://${v}`);

function StoreContact({ brand, name }) {
  const rows = [
    brand.phone && { icon: Phone, label: brand.phone, href: `tel:${digits(brand.phone)}`, k: "phone" },
    brand.whatsapp && { icon: MessageCircle, label: `WhatsApp ${brand.whatsapp}`, href: `https://wa.me/${digits(brand.whatsapp).replace("+", "")}`, k: "whatsapp" },
    brand.email && { icon: Mail, label: brand.email, href: `mailto:${brand.email}`, k: "email" },
    brand.website && { icon: Globe, label: brand.website.replace(/^https?:\/\//, ""), href: site(brand.website), k: "website" },
    brand.address && { icon: MapPin, label: brand.address, href: brand.maps_link ? site(brand.maps_link) : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(brand.address)}`, k: "address" },
    !brand.address && brand.maps_link && { icon: MapPin, label: "Find us on Google Maps", href: site(brand.maps_link), k: "maps" },
    brand.facebook && { icon: Facebook, label: "Facebook page", href: site(brand.facebook), k: "facebook" },
    brand.instagram && { icon: Instagram, label: "Instagram", href: site(brand.instagram), k: "instagram" },
    brand.google_review && { icon: Star, label: "Rate us on Google", href: site(brand.google_review), k: "google-review" },
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <section className="rounded-2xl bg-stone-900 text-amber-50 p-6 space-y-4" data-testid="verify-store-contact">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400">Contact the jeweller</p>
        {name && <p className="text-lg font-bold mt-1" style={{ fontFamily: "Cinzel, serif" }}>{name}</p>}
      </div>
      <ul className="space-y-2.5">
        {rows.map(({ icon: I, label, href, k }) => (
          <li key={k}>
            <a href={href} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-sm hover:text-amber-300 transition-colors" data-testid={`verify-contact-${k}`}>
              <I size={16} className="text-amber-400 mt-0.5 shrink-0" /><span className="whitespace-pre-line">{label}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
